/**
 * Section shadowing — assignment queue and coverage rollup Firestore schemas
 * and paths.
 *
 * These live in a dedicated top-level tree (NOT under `sectionAnalysis`, which
 * holds the feedback itself):
 * - Assignments: `sectionShadow/{courseId}/assignments/{assignmentId}`
 * - Coverage rollup: `sectionShadow/{courseId}/coverage/{coverageId}`
 *
 * `assignmentId` is deterministic (`{reviewerUid}__{sectionId}__{sectionWeek}`)
 * so a claim is idempotent — re-claiming the same target is a no-op rather than
 * a duplicate. `coverageId` is `{sectionId}__{sectionWeek}` so the backfill job
 * can look up per-week coverage directly.
 */
import {z} from 'zod';

import {FirestoreTimestampSchema} from '../../types';
import type {SectionId, SectionWeekId} from '../../../section/sectionApi';
import {ShadowRubricSchema} from '../../../section/shadowRubric';
import {SHADOW_UNSUBMIT_REFUSALS} from '../../../section/shadowDelivery';
import type {CourseId} from '../../../types/branded';

/** Root collection for shadowing queue + coverage data. */
export const SECTION_SHADOW_COLLECTION = 'sectionShadow' as const;

/** Subcollection of per-reviewer assignments. */
export const SECTION_SHADOW_ASSIGNMENTS_SUBCOLLECTION = 'assignments' as const;

/** Subcollection of per-section-week coverage rollups. */
export const SECTION_SHADOW_COVERAGE_SUBCOLLECTION = 'coverage' as const;

/** Subcollection of superseded (past) rubric versions; the current one is inline
 * on the config doc. */
export const SECTION_SHADOW_RUBRIC_HISTORY_SUBCOLLECTION =
  'rubricVersionHistory' as const;

/**
 * Subcollection of per-reviewer, per-week markers that make a required claim
 * idempotent (see {@link ShadowRequiredClaimDocumentSchema}). Server-only.
 */
export const SECTION_SHADOW_REQUIRED_CLAIMS_SUBCOLLECTION =
  'requiredClaims' as const;

/** Subcollection of reviewer-nominated "Teaching Star" moments. */
export const SECTION_SHADOW_NOMINATIONS_SUBCOLLECTION = 'nominations' as const;

/**
 * Must match the Cloud Functions export name (Firebase callable id) that
 * atomically claims the next (or an extra) shadow assignment for the caller.
 */
export const CLAIM_SHADOW_ASSIGNMENT_FUNCTION_NAME = 'claimshadowassignment';

/**
 * Why an assignment exists, and whether it counts toward the reviewer's
 * per-guaranteed-week requirement. `required` assignments are the ones the
 * reviewer took on to satisfy a guaranteed-week obligation (one per week);
 * `extra` assignments are opt-in overflow beyond that requirement and never
 * reduce it.
 */
export const ShadowAssignmentSourceSchema = z.enum(['required', 'extra']);

/** Origin of a shadowing assignment (requirement fulfillment vs. opt-in extra). */
export type ShadowAssignmentSource = z.infer<
  typeof ShadowAssignmentSourceSchema
>;

/**
 * How a claim is made:
 * - `required`: toward the reviewer's guaranteed-week requirement. Restricted
 *   to weeks they still owe and to sections whose credit slot is free (see
 *   `isCreditSlotFree`); the resulting assignment is `source: required`.
 * - `extra`: opt-in overflow (the "shadow another section" button). Draws from
 *   any wanted week; `source: extra`. Refused while a required claim is
 *   available to the reviewer.
 * - `staff`: course staff (TA and above) review one named section-week that
 *   has a recording, whether or not its week is wanted or over. The
 *   assignment is `source: extra` with `isStaffReview`, has no due date, and
 *   never touches the coverage rollup, so it takes no credit slot.
 */
export const ClaimShadowAssignmentModeSchema = z.enum([
  'required',
  'extra',
  'staff',
]);

/** Claim mode: requirement fulfillment, opt-in overflow, or a staff review. */
export type ClaimShadowAssignmentMode = z.infer<
  typeof ClaimShadowAssignmentModeSchema
>;

/** Callable request payload for `claimshadowassignment`. */
export const ClaimShadowAssignmentRequestSchema = z.object({
  /** Course to claim a shadow assignment in. */
  courseId: z.string().min(1),
  /** Claim mode; defaults to `extra` (opt-in overflow) when omitted. */
  mode: ClaimShadowAssignmentModeSchema.optional(),
  /**
   * For a `required` claim, the 1-based owed week to claim in. Omitted or null
   * ⇒ any week the reviewer still owes (the callable client sends an unset key
   * as null). Required for `staff`: the week of `sectionId` to review.
   * Ignored for `extra`.
   */
  sectionWeek: z.number().nullish(),
  /**
   * For a `staff` claim, the section to review (required there). Ignored for
   * the other modes.
   */
  sectionId: z.string().nullish(),
});

/** Request payload for `claimshadowassignment`. */
export type ClaimShadowAssignmentRequest = z.infer<
  typeof ClaimShadowAssignmentRequestSchema
>;

/**
 * Why a claim produced no assignment:
 * - `nothing-owed`: a `required` claim by a reviewer who owes nothing (in the
 *   requested week, when one was given).
 * - `waiting-for-sections`: no credit slot is free yet, but sections that have
 *   not finished or have no recording yet may still open one.
 * - `none-available`: nothing is eligible and nothing more is expected.
 * - `required-available`: an `extra` claim by a reviewer who still owes a week
 *   that has a free credit slot; they take the required review first.
 * - `contended`: every section tried was taken by a concurrent claim.
 * - `no-recording`: a `staff` claim of a section-week with no recording.
 * - `own-section`: a `staff` claim of a section the caller taught.
 */
export const ClaimShadowUnavailableReasonSchema = z.enum([
  'nothing-owed',
  'waiting-for-sections',
  'none-available',
  'required-available',
  'contended',
  'no-recording',
  'own-section',
]);

/** Why a `claimshadowassignment` call produced no assignment. */
export type ClaimShadowUnavailableReason = z.infer<
  typeof ClaimShadowUnavailableReasonSchema
>;

/** Callable response for `claimshadowassignment`. */
export const ClaimShadowAssignmentResponseSchema = z.object({
  /** The created assignment id, or null when no eligible target was available. */
  assignmentId: z.string().nullable(),
  /** Whether the assignment counts toward the requirement; set with an id. */
  source: ShadowAssignmentSourceSchema.optional(),
  /**
   * The assignment's due date (epoch ms); set with an id, absent for an
   * untimed course.
   */
  dueAtMs: z.number().optional(),
  /**
   * True when no new assignment was created: an extra review the reviewer
   * already had in the owed week was made to count instead (see
   * `pickExtraToCount`). `assignmentId` is then that existing assignment.
   */
  countedExisting: z.boolean().optional(),
  /** Why there is no assignment; set when `assignmentId` is null. */
  unavailableReason: ClaimShadowUnavailableReasonSchema.optional(),
  /**
   * With `waiting-for-sections`, when the next section that may open a credit
   * slot is scheduled to end (epoch ms). Absent when none is scheduled ahead.
   */
  nextSectionEndMs: z.number().optional(),
});

/** Response for `claimshadowassignment`. */
export type ClaimShadowAssignmentResponse = z.infer<
  typeof ClaimShadowAssignmentResponseSchema
>;

/**
 * Must match the Cloud Functions export name (Firebase callable id) that lets a
 * reviewer give back a review they have not submitted. An opt-in (`extra`)
 * review can always be given back; a `required` one only a limited number of
 * times per week, after which the callable fails with `resource-exhausted`. A
 * submitted review cannot be given back.
 */
export const UNCLAIM_SHADOW_ASSIGNMENT_FUNCTION_NAME =
  'unclaimshadowassignment';

/** Callable request payload for `unclaimshadowassignment`. */
export const UnclaimShadowAssignmentRequestSchema = z.object({
  /** Course the assignment is in. */
  courseId: z.string().min(1),
  /** Section of the assignment to give back. */
  sectionId: z.string().min(1),
  /** 1-based section week of the assignment to give back. */
  sectionWeek: z.number(),
});

/** Request payload for `unclaimshadowassignment`. */
export type UnclaimShadowAssignmentRequest = z.infer<
  typeof UnclaimShadowAssignmentRequestSchema
>;

/** Callable response for `unclaimshadowassignment`. */
export const UnclaimShadowAssignmentResponseSchema = z.object({
  /** True when the assignment was given back; false when there was none to. */
  unclaimed: z.boolean(),
});

/** Response for `unclaimshadowassignment`. */
export type UnclaimShadowAssignmentResponse = z.infer<
  typeof UnclaimShadowAssignmentResponseSchema
>;

/**
 * Must match the Cloud Functions export name (Firebase callable id) that lets a
 * reviewer take back a review they submitted: the feedback returns to a draft
 * on their still-held claim, to edit and submit again. When it is refused is
 * `getShadowUnsubmitRefusal`.
 */
export const UNSUBMIT_SHADOW_FEEDBACK_FUNCTION_NAME = 'unsubmitshadowfeedback';

/** Callable request payload for `unsubmitshadowfeedback`. */
export const UnsubmitShadowFeedbackRequestSchema =
  UnclaimShadowAssignmentRequestSchema;

/** Request payload for `unsubmitshadowfeedback`. */
export type UnsubmitShadowFeedbackRequest = z.infer<
  typeof UnsubmitShadowFeedbackRequestSchema
>;

/** Callable response for `unsubmitshadowfeedback`. */
export const UnsubmitShadowFeedbackResponseSchema = z.object({
  /** True when the review was returned to a draft. */
  unsubmitted: z.boolean(),
  /** Why it was not; absent when `unsubmitted` is true. */
  refusal: z.enum(SHADOW_UNSUBMIT_REFUSALS).optional(),
});

/** Response for `unsubmitshadowfeedback`. */
export type UnsubmitShadowFeedbackResponse = z.infer<
  typeof UnsubmitShadowFeedbackResponseSchema
>;

// ============================================================================
// Assignment document
// ============================================================================

/** Lifecycle of a shadowing assignment. */
export const ShadowAssignmentStatusSchema = z.enum(['claimed', 'submitted']);

/** Lifecycle status of a shadowing assignment. */
export type ShadowAssignmentStatus = z.infer<
  typeof ShadowAssignmentStatusSchema
>;

/**
 * A shadowing assignment: reviewer X is expected to shadow section Y, week N.
 * Document id is `{reviewerUid}__{sectionId}__{sectionWeek}`
 * (see `getShadowAssignmentId`). Uses `.loose()` for extra metadata.
 */
export const ShadowAssignmentDocumentSchema = z
  .object({
    /** Firebase Auth uid of the reviewer who must complete this shadow. */
    reviewerUid: z.string(),
    /** Firebase Auth uid of the section leader being shadowed (teacher of the week). */
    revieweeUid: z.string(),
    /** Denormalized course id (matches the `{courseId}` path segment). */
    courseId: z.string(),
    /** Section being shadowed. */
    sectionId: z.string(),
    /**
     * Denormalized section display name, snapshotted at claim time so admin
     * tables can show a name without reading the (heavy) section doc. Absent for
     * sections with no name; readers fall back to the id.
     */
    sectionName: z.string().optional(),
    /** 1-based section week being shadowed. */
    sectionWeek: z.number(),
    /** Claimed (in progress) or submitted (feedback sent). */
    status: ShadowAssignmentStatusSchema,
    /** `required` (satisfies a guaranteed-week obligation) or `extra` (overflow). */
    source: ShadowAssignmentSourceSchema,
    /** When the assignment was created (server time). */
    createdAt: FirestoreTimestampSchema,
    /** Optional soft due date for completing the shadow. Omitted ⇒ no due date. */
    dueAt: FirestoreTimestampSchema.optional(),
    /** When the reviewer submitted feedback; absent until then. */
    submittedAt: FirestoreTimestampSchema.optional(),
    /**
     * True once the reviewer has started (saved) a draft for this assignment,
     * so the queue can show a "Draft" state distinct from an untouched "To do".
     * Set by the feedback-write trigger on the first draft save; absent ⇒ not
     * started.
     */
    hasDraft: z.boolean().optional(),
    /**
     * True for a review course staff claimed of a named section-week (claim
     * mode `staff`). Such a review never touches the coverage rollup, so it
     * takes no credit slot and fulfills no review request. Absent ⇒ false.
     */
    isStaffReview: z.boolean().optional(),
  })
  .loose();

/** A stored shadowing assignment document. */
export type ShadowAssignmentDocument = z.infer<
  typeof ShadowAssignmentDocumentSchema
>;

// ============================================================================
// Required-claim marker document
// ============================================================================

/**
 * Records the required assignment a reviewer took for a section week. A
 * required claim picks its section at random, so the assignment id alone no
 * longer makes two concurrent claims by one reviewer collapse into one; the
 * claim transaction reads and writes this marker so the second claim returns
 * the first one's assignment. It also remembers the sections the reviewer gave
 * back that week, which caps how often they can and keeps those sections out
 * of their next pick. Document id is `{reviewerUid}__{sectionWeek}` (see
 * `getShadowRequiredClaimDocumentPath`).
 */
export const ShadowRequiredClaimDocumentSchema = z.object({
  /**
   * The reviewer's latest required assignment for this week. It blocks another
   * required claim for the week only while that assignment is unsubmitted.
   * Absent after they give it back, until they take another.
   */
  assignmentId: z.string().optional(),
  /** Sections whose required review the reviewer gave back this week. */
  givenBackSectionIds: z.array(z.string()).optional(),
  /** When the current or most recent claim was made (server time). */
  createdAt: FirestoreTimestampSchema.optional(),
});

/** A stored required-claim marker document. */
export type ShadowRequiredClaimDocument = z.infer<
  typeof ShadowRequiredClaimDocumentSchema
>;

// ============================================================================
// Coverage rollup document
// ============================================================================

/**
 * Per-section-week coverage rollup, maintained by the recording, feedback and
 * assignment writers. It is also the index a claim picks from: one exists for
 * every section-week whose recording has landed, so a claim reads these docs
 * rather than every section and recording doc. Uses `.loose()` for extra
 * metadata.
 */
export const ShadowCoverageDocumentSchema = z
  .object({
    /** Denormalized course id. */
    courseId: z.string(),
    /** Section this rollup is for. */
    sectionId: z.string(),
    /**
     * Denormalized section display name, so admin tables show a name without
     * reading the (heavy) section doc. Set by the first coverage writer that
     * knows it (a claim); absent otherwise — readers fall back to the id.
     */
    sectionName: z.string().optional(),
    /** 1-based section week this rollup is for. */
    sectionWeek: z.number(),
    /**
     * Section leader who taught this week (the reviewee). Lets a section leader
     * query their own section-weeks (`where revieweeUid == uid`) for the "My
     * sections" view. Set by every coverage writer.
     */
    revieweeUid: z.string(),
    /**
     * True while the section-week's recording doc exists, so the section can
     * be shadowed. Maintained by the recording trigger
     * (`onsectionrecordingwrite`); absent ⇒ no recording has landed.
     */
    hasRecording: z.boolean().optional(),
    /**
     * When the section's scheduled slot ends for this week, written with
     * `hasRecording`. A section is shadowable only from this instant on, so a
     * recording that lands early does not open it. Absent ⇒ untimed.
     */
    scheduledEndAt: FirestoreTimestampSchema.optional(),
    /** Number of reviewers currently assigned to this section week. */
    assignedCount: z.number(),
    /** Number of assigned reviewers who have submitted feedback. */
    submittedCount: z.number(),
    /** Reviewer uids currently assigned (for display + dedupe). */
    reviewerUids: z.array(z.string()),
    /**
     * True when an as-yet-unfulfilled review is wanted for this section-week —
     * set by the reviewee (request-own) or by the backfill for a guaranteed
     * week, and cleared once a review is submitted. Absent ⇒ false. The
     * reviewers' self-pick queue draws from section-weeks with this set.
     */
    reviewRequested: z.boolean().optional(),
    /**
     * The unsubmitted required claims holding this section-week's credit
     * slots, keyed by reviewer uid. An entry is written when the claim is
     * created and removed when its reviewer submits or gives the review back;
     * its hold lapses at `until` (see `countTakenCreditSlots`). Absent ⇒ none.
     */
    requiredHolds: z
      .record(
        z.string(),
        z.object({
          /** When the hold lapses: the claim's `dueAt`. */
          until: FirestoreTimestampSchema,
        }),
      )
      .optional(),
    /** Server time of the last rollup write. */
    updatedAt: FirestoreTimestampSchema,
  })
  .loose();

/** A stored shadowing coverage rollup document. */
export type ShadowCoverageDocument = z.infer<
  typeof ShadowCoverageDocumentSchema
>;

// ============================================================================
// Nomination document ("Teaching Star" moments)
// ============================================================================

/**
 * A reviewer-nominated "Teaching Star" moment: one highlighted transcript
 * moment from a moderation-approved shadow feedback, denormalized so any
 * section leader in the course can browse and watch it without reading the
 * underlying feedback doc.
 *
 * Lives at `sectionShadow/{courseId}/nominations/{nominationId}` with a
 * deterministic id (see {@link getShadowNominationId}) so re-approving the same
 * feedback reconciles the set rather than duplicating it. Server-written only.
 * Uses `.loose()` for extra metadata.
 */
export const ShadowNominationDocumentSchema = z
  .object({
    /** Denormalized course id (matches the `{courseId}` path segment). */
    courseId: z.string(),
    /** Section the nominated moment is from. */
    sectionId: z.string(),
    /** 1-based section week the nominated moment is from. */
    sectionWeek: z.number(),
    /** Reviewer who authored the feedback (and nominated the moment). */
    reviewerUid: z.string(),
    /** Section leader who taught the week (owner of the showcased teaching). */
    revieweeUid: z.string(),
    /**
     * Who nominated the moment. Currently always the reviewer; kept discrete so
     * a future curator flow (staff nominating another SL's moment) has a home.
     */
    nominatedByUid: z.string(),
    /** Id of the `assignments` doc whose feedback this moment came from. */
    assignmentId: z.string(),
    /** Rubric field key the moment was captured under (a `momentList` field). */
    fieldKey: z.string(),
    /** 0-based index of the moment within its field's moment array. */
    momentIndex: z.number(),
    /** Inclusive start offset within the recording part (e.g. `HH:MM:SS`). */
    start: z.string(),
    /** Inclusive end offset within the recording part (e.g. `HH:MM:SS`). */
    end: z.string(),
    /** 1-based recording part the moment falls in; absent for single-part. */
    part: z.number().optional(),
    /** The reviewer's note about the moment. */
    note: z.string(),
    /**
     * Set true by the reviewee to share their showcased moment on the Teaching
     * Stars leaderboard for other section leaders to watch (opt-in). Absent ⇒
     * private: only the reviewee (and staff) can see it. The only field the
     * reviewee may write.
     */
    sharedByReviewee: z.boolean().optional(),
    /**
     * Uids of section leaders who starred this moment. Its length is the star
     * count and drives the browse-page sort (most-starred first). The nominator
     * is auto-starred at creation, so a freshly nominated moment has one star.
     * Server-maintained via the `starshadowmoment` callable.
     */
    starredBy: z.array(z.string()).optional(),
    /** When the nomination was created (server time). */
    createdAt: FirestoreTimestampSchema,
  })
  .loose();

/** A stored "Teaching Star" moment nomination document. */
export type ShadowNominationDocument = z.infer<
  typeof ShadowNominationDocumentSchema
>;

/**
 * Must match the Cloud Functions export name (Firebase callable id) that lets a
 * reviewee share or un-share one of their own showcased moments (the opt-in).
 * Client writes to the nominations collection are denied by rules, so this
 * callable is the only way to set `sharedByReviewee`.
 */
export const SET_SHADOW_MOMENT_SHARED_FUNCTION_NAME = 'setshadowmomentshared';

/** Callable request payload for `setshadowmomentshared`. */
export const SetShadowMomentSharedRequestSchema = z.object({
  /** Course the nomination belongs to. */
  courseId: z.string().min(1),
  /** The nomination to share/un-share (must be one the caller is the reviewee of). */
  nominationId: z.string().min(1),
  /** True to share on the leaderboard; false to make it private again. */
  shared: z.boolean(),
});

/** Request payload for `setshadowmomentshared`. */
export type SetShadowMomentSharedRequest = z.infer<
  typeof SetShadowMomentSharedRequestSchema
>;

/** Callable response for `setshadowmomentshared`. */
export const SetShadowMomentSharedResponseSchema = z.object({
  /** The resulting shared state (echoes the request on success). */
  shared: z.boolean(),
});

/** Response for `setshadowmomentshared`. */
export type SetShadowMomentSharedResponse = z.infer<
  typeof SetShadowMomentSharedResponseSchema
>;

/**
 * Must match the Cloud Functions export name (Firebase callable id) that lets a
 * section leader star or un-star a teaching-star moment. Client writes to the
 * nominations collection are denied by rules, so stars go through this callable
 * (it maintains the `starredBy` array server-side).
 */
export const STAR_SHADOW_MOMENT_FUNCTION_NAME = 'starshadowmoment';

/** Callable request payload for `starshadowmoment`. */
export const StarShadowMomentRequestSchema = z.object({
  /** Course the nomination belongs to. */
  courseId: z.string().min(1),
  /** The nomination to star/un-star. */
  nominationId: z.string().min(1),
  /** True to star; false to remove the caller's star. */
  starred: z.boolean(),
});

/** Request payload for `starshadowmoment`. */
export type StarShadowMomentRequest = z.infer<
  typeof StarShadowMomentRequestSchema
>;

/** Callable response for `starshadowmoment`. */
export const StarShadowMomentResponseSchema = z.object({
  /** Whether the caller now stars the moment. */
  starred: z.boolean(),
  /** The resulting star count (`starredBy` length). */
  starCount: z.number(),
});

/** Response for `starshadowmoment`. */
export type StarShadowMomentResponse = z.infer<
  typeof StarShadowMomentResponseSchema
>;

// ============================================================================
// Ids + paths
// ============================================================================

/**
 * Deterministic assignment document id: `{reviewerUid}__{sectionId}__{sectionWeek}`.
 * Making the id a pure function of its target keeps claims idempotent.
 */
export function getShadowAssignmentId({
  reviewerUid,
  sectionId,
  sectionWeek,
}: {
  reviewerUid: string;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
}): string {
  return `${reviewerUid}__${sectionId}__${String(sectionWeek)}`;
}

/**
 * Deterministic coverage document id: `{sectionId}__{sectionWeek}`.
 */
export function getShadowCoverageId({
  sectionId,
  sectionWeek,
}: {
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
}): string {
  return `${sectionId}__${String(sectionWeek)}`;
}

/** Assignments collection path: `sectionShadow/{courseId}/assignments`. */
export function getShadowAssignmentsCollectionPath({
  courseId,
}: {
  courseId: CourseId;
}): string {
  return `${SECTION_SHADOW_COLLECTION}/${courseId}/${SECTION_SHADOW_ASSIGNMENTS_SUBCOLLECTION}`;
}

/** Single assignment document path. */
export function getShadowAssignmentDocumentPath({
  courseId,
  assignmentId,
}: {
  courseId: CourseId;
  assignmentId: string;
}): string {
  return `${getShadowAssignmentsCollectionPath({courseId})}/${assignmentId}`;
}

/**
 * Path of the marker recording a reviewer's required claim for a section week.
 *
 * @param courseId - Owning course.
 * @param reviewerUid - The reviewer who claims.
 * @param sectionWeek - 1-based section week the required claim is for.
 * @return The marker document path.
 */
export function getShadowRequiredClaimDocumentPath({
  courseId,
  reviewerUid,
  sectionWeek,
}: {
  courseId: CourseId;
  reviewerUid: string;
  sectionWeek: number;
}): string {
  return `${SECTION_SHADOW_COLLECTION}/${courseId}/${SECTION_SHADOW_REQUIRED_CLAIMS_SUBCOLLECTION}/${reviewerUid}__${String(sectionWeek)}`;
}

/** Coverage collection path: `sectionShadow/{courseId}/coverage`. */
export function getShadowCoverageCollectionPath({
  courseId,
}: {
  courseId: CourseId;
}): string {
  return `${SECTION_SHADOW_COLLECTION}/${courseId}/${SECTION_SHADOW_COVERAGE_SUBCOLLECTION}`;
}

/** Single coverage document path. */
export function getShadowCoverageDocumentPath({
  courseId,
  coverageId,
}: {
  courseId: CourseId;
  coverageId: string;
}): string {
  return `${getShadowCoverageCollectionPath({courseId})}/${coverageId}`;
}

/**
 * Deterministic nomination document id:
 * `{reviewerUid}__{sectionId}__{sectionWeek}__{fieldKey}__{momentIndex}`.
 * Keying on the moment's position makes re-approval idempotent (same moment →
 * same doc) and lets the approve step reconcile a changed highlight set.
 */
export function getShadowNominationId({
  reviewerUid,
  sectionId,
  sectionWeek,
  fieldKey,
  momentIndex,
}: {
  reviewerUid: string;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  fieldKey: string;
  momentIndex: number;
}): string {
  return `${reviewerUid}__${sectionId}__${String(sectionWeek)}__${fieldKey}__${String(momentIndex)}`;
}

/** Nominations collection path: `sectionShadow/{courseId}/nominations`. */
export function getShadowNominationsCollectionPath({
  courseId,
}: {
  courseId: CourseId;
}): string {
  return `${SECTION_SHADOW_COLLECTION}/${courseId}/${SECTION_SHADOW_NOMINATIONS_SUBCOLLECTION}`;
}

/** Single nomination document path. */
export function getShadowNominationDocumentPath({
  courseId,
  nominationId,
}: {
  courseId: CourseId;
  nominationId: string;
}): string {
  return `${getShadowNominationsCollectionPath({courseId})}/${nominationId}`;
}

// ============================================================================
// Course shadowing config
// ============================================================================

/**
 * Per-course shadowing settings, stored on the `sectionShadow/{courseId}` doc
 * (the parent of the assignments + coverage subcollections). Admin-editable.
 * The weekly review deadline is NOT here: it derives from the course doc's
 * schedule and `shadowReviewDueWeekday` (see `getShadowReviewDueMs`). Only an
 * optional per-section minimum on top of it is (`minDaysAfterSection`).
 */
export const ShadowConfigDocumentSchema = z
  .object({
    /**
     * 1-based section weeks with guaranteed coverage: every section is wanted
     * (each SL owes a review for it). Absent/empty ⇒ no guaranteed weeks.
     */
    guaranteedCoverageWeeks: z.array(z.number()).optional(),
    /**
     * How many reviews each section leader owes for every guaranteed week.
     * Read through `resolveRequiredShadowReviewsPerWeek`, which supplies the
     * default when this is absent or not a positive integer.
     */
    requiredReviewsPerWeek: z.number().optional(),
    /**
     * Least time, in days, a reviewer gets after the section they review ends:
     * a claim's due date is pushed past the weekly deadline when needed (see
     * `getShadowReviewDueMsForSection`). Absent ⇒ every review is due at the
     * weekly deadline.
     */
    minDaysAfterSection: z.number().optional(),
    /**
     * The current feedback rubric, inline (its `version` is the current version
     * id). Kept here so reviewers get it with the config read they already do;
     * superseded versions move to the `rubricVersionHistory` subcollection.
     * Absent until the rubric is seeded/published.
     */
    rubric: ShadowRubricSchema.optional(),
  })
  .loose();

/** A stored per-course shadowing config document. */
export type ShadowConfigDocument = z.infer<typeof ShadowConfigDocumentSchema>;

/** Config document path: `sectionShadow/{courseId}`. */
export function getShadowConfigDocumentPath({
  courseId,
}: {
  courseId: CourseId;
}): string {
  return `${SECTION_SHADOW_COLLECTION}/${courseId}`;
}

/** Past-rubric-version history collection: `sectionShadow/{courseId}/rubricVersionHistory`. */
export function getShadowRubricHistoryCollectionPath({
  courseId,
}: {
  courseId: CourseId;
}): string {
  return `${SECTION_SHADOW_COLLECTION}/${courseId}/${SECTION_SHADOW_RUBRIC_HISTORY_SUBCOLLECTION}`;
}

/** One superseded rubric version: `.../rubricVersionHistory/{versionId}`. */
export function getShadowRubricHistoryDocumentPath({
  courseId,
  versionId,
}: {
  courseId: CourseId;
  versionId: string;
}): string {
  return `${getShadowRubricHistoryCollectionPath({courseId})}/${versionId}`;
}

// ============================================================================
// request-own-review callable
// ============================================================================

/**
 * Callable id for a section leader requesting an (additional) review of their
 * own section-week.
 */
export const REQUEST_SHADOW_REVIEW_FUNCTION_NAME = 'requestshadowreview';

/** Callable request payload for `requestshadowreview`. */
export const RequestShadowReviewRequestSchema = z.object({
  /** Course the section is in. */
  courseId: z.string().min(1),
  /** The caller's own section to request a review of. */
  sectionId: z.string().min(1),
  /** 1-based section week to request a review for. */
  sectionWeek: z.number(),
});

/** Request payload for `requestshadowreview`. */
export type RequestShadowReviewRequest = z.infer<
  typeof RequestShadowReviewRequestSchema
>;

/** Callable response for `requestshadowreview`. */
export const RequestShadowReviewResponseSchema = z.object({
  /** True if a new request was recorded; false if one was already outstanding. */
  requested: z.boolean(),
});

/** Response for `requestshadowreview`. */
export type RequestShadowReviewResponse = z.infer<
  typeof RequestShadowReviewResponseSchema
>;
